import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ActionDeleteIcon } from '../../assets/icons/action-delete.svg';
import { ReactComponent as ActionEditIcon } from '../../assets/icons/action-edit.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as ActionViewIcon } from '../../assets/icons/eye-fill-icon.svg';
import { ReactComponent as HistoryIcon } from '../../assets/icons/history.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import {
  AvailableGuestCharging,
  RequestGuestChargingInterface,
} from '../../interface/guest-charging.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchGuestCharging,
  fetchListFilters,
  updateGuestFilters,
  fetchGuestChargingHistory,
  setGuestChargingFilterData,
  setSortData,
} from '../../redux/slice/GuestChargingSlice';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../shared-components/delete-warning/DeleteWarning';
import DisableSwitchWarning from '../../shared-components/disable-warning/DisableWarning';
import DropDownMenu from '../../shared-components/drop-down-menu/DropDownMenu';
import ExportButton from '../../shared-components/export-button/ExportButton';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotificaion';
import SwitchButton from '../../shared-components/switch-button-field/SwitchButton';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import { FormatUtcString } from '../../shared-functions/FormatUtcString';
import guestChargingApi from '../../utils/api/guest-charging';

import GuestChargingHistory from './components/GuestChargingHistory';
import GuestChargingViewModal from './components/GuestChargingView';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
  closeModal: () => void;
}

/**
 * Station management page
 */
function GuestCharging() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    guestCharging,
    page,
    limit,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
  } = useAppSelector((state) => state.guestCharging);
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
    /**
     * Close modal
     */
    closeModal: () => setModalBoxStatus('close'),
  });
  const { sortBy, sortField } = useAppSelector((state) => state.guestCharging);

  /**
   * Handle the fetch data for Api call function
   * @param data
   */
  const fetchData = (data: RequestGuestChargingInterface) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchGuestCharging({
          ...data,
        }),
      );
    }
  };

  /**
   * Delete station API call
   * @param rowId
   */
  const handleDeleteGuestCharging = (rowId?: string | null) => {
    if (rowId) {
      guestChargingApi
        .deleteGuestCharging(rowId)
        .then(() => {
          setModalBoxStatus('open');
          setModalBoxDetail({
            title: '',
            content: (
              <SuccessNotification
                title="Guest charging deleted successfully..."
                description=""
                notificationCloseTimer={3000}
                onNotificationClose={() => {
                  setModalBoxStatus('close');
                  // dispatch(updateSingleStation(response.data));
                }}
              />
            ),
            notificationModal: true,
            header: false,
            /**
             * Close modal with function to dispatch station details
             */
            closeModal: () => {
              setModalBoxStatus('close');
              // dispatch(updateSingleStation(response.data));
            },
          });
          fetchData({ page: DefaultApiLimit.page, limit });
        })
        .catch((error) => {
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || 'Something went wrong!',
              },
            }),
          );
        });
    } else {
      setModalBoxDetail({
        title: '',
        content: null,
        notificationModal: false,
        header: true,
        /**
         * Close modal
         */
        closeModal: () => setModalBoxStatus('close'),
      });
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: 200,
            message: 'Something went wrong!',
          },
        }),
      );
    }
  };

  /**
   * Confirm the status change function
   * @param id
   * @param checked
   */
  const confirmStatusChange = (id: string, checked: boolean) => {
    guestChargingApi
      .updateGuestStatus(id, { isActive: checked })
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          content: (
            <SuccessNotification
              title="Status Changed successfully..."
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                fetchData({ page: DefaultApiLimit.page, limit });
                // dispatch(updateSingleStation(response.data));
              }}
            />
          ),
          notificationModal: true,
          header: false,
          /**
           * Close modal with function to dispatch station details
           */
          closeModal: () => {
            setModalBoxStatus('close');
            // dispatch(updateSingleStation(response.data));
          },
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handles the availability status change for a device
   * Shows a confirmation modal when disabling a device
   * @param {AvailableDevice} device - The device to update
   * @param {boolean} checked - The new availability status
   */
  const handleAvailabilityChange = (device: AvailableGuestCharging, checked: boolean) => {
    if (checked) {
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: 'Disable Device Warning',
        content: (
          <DisableSwitchWarning
            selectedId={device._id}
            onCancel={() => setModalBoxStatus('close')}
            message="Making your facility available for public or third-party use opens new revenue opportunities and boosts network accessibility, but also requires sharing control and adhering to service agreements."
            onConfirm={() => confirmStatusChange(device._id, checked)}
          />
        ),
        notificationModal: true,
        header: false,
        /**
         * Close modal
         */
        closeModal: () => setModalBoxStatus('close'),
      });
    } else {
      confirmStatusChange(device._id, checked);
    }
  };
  /**
   * Renders the custom data for the availability column
   * @param {AvailableDevice} device - The device data
   * @returns {JSX.Element} The rendered availability toggle cell
   */
  const renderAvailability = (device: AvailableGuestCharging) => (
    <div className="flex justify-center">
      <div key={device.id}>
        <SwitchButton
          checked={device.isActive}
          change={(checked: boolean) => handleAvailabilityChange(device, checked)}
        />
      </div>
    </div>
  );

  /**
   * Renders the time
   * @param device
   * @returns
   */
  const renderTime = (device: AvailableGuestCharging) => (
    <div className="charging-time-list">
      <div className="first-time">{device.time[0]}</div>
      <div className="count">{device.time.length - 1}</div>
    </div>
  );

  /**
   * Delete station warning popup
   */
  const handleDeleteGuestCharingWarning = (rowId?: string | null) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      content: (
        <DeleteWarning
          selectedRowId={rowId}
          title="Are you sure want to delete this Station?"
          description="Deleting your station is permanent and cannot be undone. All the data, settings, and history will be lost. Are you sure you want to proceed?"
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={handleDeleteGuestCharging}
        />
      ),
      notificationModal: true,
      header: false,
      /**
       * Close modal
       */
      closeModal: () => setModalBoxStatus('close'),
    });
  };

  /**
   * Edit the guest charging
   * @param rowId
   */
  const handleEditGuestCharging = (rowId?: string | null) => {
    navigate(`${rowId}`);
  };
  /**
   * View the guest charging
   * @param rowId
   */
  const handleViewGuestCharging = (rowId?: string | null) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      header: false,
      notificationModal: false,
      content: <GuestChargingViewModal rowId={rowId} />,
      /**
       * close the modal box
       * @returns
       */
      closeModal: () => setModalBoxStatus('close'),
    });
  };
  /**
   * View the guest charging
   * @param rowId
   */
  const handleHistoryOfGuestCharging = (rowId?: string | null) => {
    setModalBoxStatus('open');
    dispatch(fetchGuestChargingHistory({ id: rowId || '' }));
    setModalBoxDetail({
      title: '',
      header: false,
      notificationModal: false,
      content: <GuestChargingHistory />,
      /**
       * close the modal box
       * @returns
       */
      closeModal: () => setModalBoxStatus('close'),
    });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setGuestChargingFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    setModalBoxStatus('close');
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      page: pageNumber,
      limit,
    });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
    });
  };

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    guestChargingApi
      .fetchGuestChargingExport({ ...filters })
      .then((response) => {
        // Convert data to CSV format
        const headers = [
          'Consumer Id',
          'Name',
          'Phone Number',
          'Email',
          'Assigned Station',
          'Device',
          'Date',
          'Time',
          'Duration',
          'status',
        ];
        const rows = response.data.results.map((row: AvailableGuestCharging) => [
          row.accountCustomId || '',
          row.name || '',
          row.phoneNumber || '',
          row.email || '',
          row.chargingPointSerials || '',
          `${ConvertUTCtoDate(row.fromDate)} - ${ConvertUTCtoDate(row.toDate)}` || '',
          `${FormatUtcString(ConvertUTCtoHHMM(row.timeSlots[0].startTime))} - ${FormatUtcString(ConvertUTCtoHHMM(row.timeSlots[0].endTime))} (${row.timeSlots.length - 1})` ||
            '',
          row.duration || '',
          row.isActive ? 'Available' : 'Unavailable',
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'guest-charging-list.csv');
          } else {
            exportPDF(headers, rows, 'guest-charging-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  const column = [
    {
      label: 'S. No',
      accessorKey: 's_no' as keyof AvailableGuestCharging,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (station: AvailableGuestCharging, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 50,
    },
    {
      label: 'Consumer Id',
      accessorKey: 'accountCustomId' as keyof AvailableGuestCharging,
      sortAllow: true,
    },
    {
      label: 'Name',
      accessorKey: 'name' as keyof AvailableGuestCharging,
      sortAllow: true,
    },
    {
      label: 'Phone Number',
      accessorKey: 'phoneNumber' as keyof AvailableGuestCharging,
      sortAllow: true,
    },
    {
      label: 'Email',
      accessorKey: 'email' as keyof AvailableGuestCharging,
      sortAllow: true,
    },
    {
      label: 'Assigned Station',
      accessorKey: 'chargingStationNames' as keyof AvailableGuestCharging,
      sortAllow: false,
    },
    {
      label: 'Device',
      accessorKey: 'chargingPointSerials' as keyof AvailableGuestCharging,
      sortAllow: false,
    },
    {
      label: 'Date',
      accessorKey: 'date' as keyof AvailableGuestCharging,
      sortAllow: false,
      /**
       * Date view
       * @param station
       * @param index
       */
      customData: (station: AvailableGuestCharging) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {ConvertUTCtoDate(station.fromDate)} - {ConvertUTCtoDate(station.toDate)}
        </div>
      ),
    },
    {
      label: 'Time',
      accessorKey: 'time' as keyof AvailableGuestCharging,
      sortAllow: false,
      columnWidth: 160,
      customData: renderTime,
    },
    {
      label: 'Duration',
      accessorKey: 'duration' as keyof AvailableGuestCharging,
      sortAllow: false,
    },
    {
      label: 'status',
      accessorKey: 'isAvailable' as keyof AvailableGuestCharging,
      customData: renderAvailability,
    },
    {
      label: 'Actions',
      accessorKey: '' as keyof AvailableGuestCharging,
      columnWidth: 60,
      /**
       * Action button in table
       * @param station
       * @param index
       */
      customData: (station: AvailableGuestCharging) => (
        <DropDownMenu
          rowId={station.id}
          dropDownList={[
            {
              icon: <ActionEditIcon />,
              content: 'Edit',
              onClickFunc: handleEditGuestCharging,
              className: 'primary-action',
            },
            {
              icon: <ActionViewIcon />,
              content: 'View',
              onClickFunc: handleViewGuestCharging,
              className: 'primary-action',
            },
            {
              icon: <HistoryIcon />,
              content: 'History',
              onClickFunc: handleHistoryOfGuestCharging,
              className: 'primary-action',
            },
            {
              icon: <ActionDeleteIcon />,
              content: 'Delete',
              /**
               *
               */
              onClickFunc: handleDeleteGuestCharingWarning,
            },
          ]}
          lineBetweenElement
        />
      ),
    },
  ];

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      limit: DefaultApiLimit.limit,
      page: DefaultApiLimit.page,
    });
  }, [currentFacilityTagsList, filters]);

  return (
    <div className="guest-charging-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={modalBoxDetail.closeModal}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="station-management-header">
        <PageTitle
          title="Guest Charging"
          buttonLabel="Add Guest"
          buttonSvgIcon={<PlusIcon />}
          buttonType="submit"
          onClickButton={() => navigate('add')}
        />
      </div>
      <div className="table-container">
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={guestCharging}
          currentPage={page}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          totalPages={totalPages}
          totalResults={totalResults}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          footerLeft={
            <div className="table-bottom-button-list">
              <div className="table-button-item">
                <ExportButton
                  label="Export Data"
                  buttonList={[
                    {
                      key: 'pdf',
                      allow: true,
                      /**
                       * Export pdf function
                       * @returns
                       */
                      onClickFunction: () => exportDataPdfOrCSV('pdf'),
                    },
                    {
                      key: 'csv',
                      allow: true,
                      /**
                       * Export csv function
                       * @returns
                       */
                      onClickFunction: () => exportDataPdfOrCSV('csv'),
                    },
                  ]}
                  disabled={guestCharging.length === 0}
                  loader={exportLoader}
                />
              </div>
            </div>
          }
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateGuestFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
        />
      </div>
    </div>
  );
}

export default GuestCharging;
